import {
  Injectable,
  Injector,
  PLATFORM_ID,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { User } from '@angular/fire/auth';
import {
  Storage,
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from '@angular/fire/storage';
import {
  DocumentData,
  Firestore,
  QueryConstraint,
  QueryDocumentSnapshot,
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import {
  Observable,
  combineLatest,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';
import { ProjectAccessService } from './project-access';
import { isProjectOwner, ProjectRole, resolveBuyerSideRole } from '../models/project-access.model';

export interface ThumbnailResponse {
  thumbnailUrl: string;
  cached: boolean;
}

export type CommunityCursor = QueryDocumentSnapshot<DocumentData> | null;

export interface CommunityPage {
  items: any[];
  cursor: CommunityCursor;
  exhausted: boolean;
}

/** A project, tagged with what the current user may actually do to it. The
 *  double-underscore marks these as view-layer additions, never persisted. */
export interface TaggedProject {
  [key: string]: any;
  __role: ProjectRole;
  __canDelete: boolean;
  __canAdminister: boolean;
}

const AGENT_API_BASE =
  'https://agent-coder-api-861501658886.us-central1.run.app/api';

const COMMUNITY_OVERFETCH_FACTOR = 3;
const COMMUNITY_MAX_TOPUP_ROUNDS = 5;

/**
 * ════════════════════════════════════════════════════════════════════════
 * FIELDS ONLY THE OWNER MAY WRITE.
 *
 * updateProject() used to take `...data` and pass it straight to updateDoc,
 * authorised by `user_ids.includes(uid)`. So ANY member could send
 * { isDeleted: true } and bin a project they did not create, or
 * { isPrivate: false } and publish someone else's work to the community page.
 * With a single-member array nobody noticed. The moment a teammate is invited,
 * that is four people who can delete your project.
 *
 * These are the fields that destroy, expose, or re-home a project. They belong
 * to the one person in created_by. Everything else (a rename, a thumbnail) any
 * member may write.
 * ════════════════════════════════════════════════════════════════════════
 */
const OWNER_ONLY_FIELDS = new Set([
  'isDeleted',
  'deletedOn',
  'deletedAt',
  'permanently_removed',
  'isPrivate',
  'isPublished',
  'customDomain',
  'created_by',
  'createdBy',
  'user_ids',
]);

function byUpdatedAtDesc(a: any, b: any): number {
  const dateA = new Date(a?.updated_at || a?.created_at || 0).getTime();
  const dateB = new Date(b?.updated_at || b?.created_at || 0).getTime();
  return dateB - dateA;
}

function isVisibleCommunityProject(
  project: any,
  excludeUid: string | null,
): boolean {
  if (project?.isDeleted === true) return false;
  if (project?.permanently_removed === true) return false;
  if (excludeUid && project?.user_ids?.includes(excludeUid)) return false;
  return true;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly injector = inject(Injector);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  constructor(
    private snack: SnackbarService,
    private firestore: Firestore,
    private storage: Storage,
    private authService: AuthService,
    private http: HttpClient,
    private access: ProjectAccessService,
  ) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  private authHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  async createUserIfNew(userData: any) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const userRef = doc(this.firestore, 'users', user.uid);
      const snap = await this.inCtx(() => getDoc(userRef));

      if (!snap.exists()) {
        await setDoc(userRef, {
          ...userData,
          uid: user.uid,
          email: user.email || null,
          displayName: user.displayName || null,
          credits: 10,
          photoURL: user.photoURL || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        this.snack.showSuccess('User profile created successfully');
      }
    } catch (err: any) {
      this.snack.showError(`Failed to create user: ${err.message}`);
    }
  }

  async updateUser(data: any) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const userRef = doc(this.firestore, 'users', user.uid);
      await updateDoc(userRef, { ...data, updatedAt: serverTimestamp() });

      this.snack.showSuccess('User profile updated');
    } catch (err: any) {
      this.snack.showError(`Failed to update user: ${err.message}`);
    }
  }

  getCurrentUserProfile(): Observable<DocumentData | null> {
    return this.authService.user$.pipe(
      switchMap((user) => {
        if (!user) {
          this.snack.showError('You must be logged in');
          return of(null);
        }
        return from(
          this.inCtx(async () => {
            const userRef = doc(this.firestore, 'users', user.uid);
            const snap = await getDoc(userRef);
            return snap.exists() ? snap.data() : null;
          }),
        );
      }),
    );
  }

  /**
   * Writes BOTH created_by (the owner) and user_ids (membership), because they
   * are different facts and both are needed. The old version wrote camelCase
   * `createdBy` and no user_ids at all — so a project made here had no owner
   * any check could find, and never appeared in getMyProjects.
   *
   * created_by is snake_case because that is what the agent backend writes and
   * what every project doc in Firestore actually carries. One spelling.
   */
  async createProject(data: any) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const projectsRef = collection(this.firestore, 'projects');
      await addDoc(projectsRef, {
        ...data,
        created_by: user.uid,
        user_ids: [user.uid],
        createdAt: serverTimestamp(),
      });
      this.snack.showSuccess('Project created successfully');
    } catch (err: any) {
      this.snack.showError(`Failed to create project: ${err.message}`);
    }
  }

  /**
   * The buyer-side project list: what you own, PLUS what you've been invited
   * onto. Both live in user_ids, so one query gets them — but they are NOT the
   * same thing, and a shared project must not render a Delete button.
   *
   * Every row carries __role, and the UI reads that. Nothing downstream may
   * infer ownership from being in this list.
   */
  getMyProjects(): Observable<TaggedProject[]> {
    if (this.isServer) return of([]);

    return this.authService.user$.pipe(
      filter((user): user is User => !!user),
      switchMap((user) =>
        this.inCtx(() => {
          const q = query(
            collection(this.firestore, 'projects'),
            where('user_ids', 'array-contains', user.uid),
          );
          return (collectionData(q, { idField: 'docId' }) as Observable<any[]>).pipe(
            map((projects) =>
              projects.map((p) => {
                const role = resolveBuyerSideRole(p, user.uid);
                return {
                  ...p,
                  __role: role,
                  __canDelete: role === 'owner',
                  __canAdminister: role === 'owner',
                } as TaggedProject;
              }),
            ),
          );
        }),
      ),
      map((projects) => [...projects].sort(byUpdatedAtDesc)),
    );
  }

  /** Only the ones you actually own. For anything that spends, deletes, or
   *  commits — a project picker on a billing screen, say. */
  getMyOwnedProjects(): Observable<TaggedProject[]> {
    return this.getMyProjects().pipe(
      map((projects) => projects.filter((p) => p.__role === 'owner')),
    );
  }

  async getProjectDocsByIds(projectIds: string[]): Promise<any[]> {
    if (!projectIds.length) return [];

    const chunks: string[][] = [];
    for (let i = 0; i < projectIds.length; i += 10) {
      chunks.push(projectIds.slice(i, i + 10));
    }

    return this.inCtx(async () => {
      const projectsRef = collection(this.firestore, 'projects');
      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const q = query(projectsRef, where('project_id', 'in', chunk));
          const snap = await getDocs(q);
          return snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
        }),
      );
      return results.flat();
    });
  }

  getRevertPoints(projectId: string): Observable<any[]> {
    return this.authService.user$.pipe(
      take(1),
      switchMap((user) => {
        if (!user) {
          return throwError(() => new Error('User not authenticated'));
        }
        return from(user.getIdToken()).pipe(
          switchMap((token) =>
            this.http.get<any[]>(
              `${AGENT_API_BASE}/projects/${projectId}/revert/points`,
              { headers: this.authHeaders(token) },
            ),
          ),
        );
      }),
    );
  }

  revertProject(projectId: string, deploymentId: string): Observable<any> {
    return this.authService.user$.pipe(
      take(1),
      switchMap((user) => {
        if (!user) {
          return throwError(() => new Error('User not authenticated'));
        }
        return from(user.getIdToken()).pipe(
          switchMap((token) =>
            this.http.post(
              `${AGENT_API_BASE}/projects/${projectId}/revert`,
              { deployment_id: deploymentId },
              { headers: this.authHeaders(token) },
            ),
          ),
        );
      }),
    );
  }

  downloadProject(projectId: string): Observable<Blob> {
    return this.authService.user$.pipe(
      take(1),
      switchMap((user) => {
        if (!user) {
          return throwError(() => new Error('User not authenticated'));
        }
        return from(user.getIdToken());
      }),
      switchMap((token) =>
        this.http.get(`${AGENT_API_BASE}/projects/${projectId}/download`, {
          headers: this.authHeaders(token),
          responseType: 'blob',
        }),
      ),
    );
  }

  /** Loads a project by its FIRESTORE DOC ID and returns the raw data. */
  private async loadProjectDoc(projectDocId: string): Promise<any> {
    const projectRef = doc(this.firestore, 'projects', projectDocId);
    const snap = await this.inCtx(() => getDoc(projectRef));
    if (!snap.exists()) throw new Error('Project not found');
    return { ref: projectRef, data: snap.data() };
  }

  /**
   * A member (owner OR teammate) may update a project — but ONLY the harmless
   * fields. Anything in OWNER_ONLY_FIELDS is rejected unless the caller is the
   * person in created_by.
   *
   * The old version checked user_ids and then forwarded the whole payload. It
   * did not care WHAT was being written. That is how { isDeleted: true } from a
   * teammate became a legitimate write.
   */
  async updateProject(projectId: string, data: any) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const { ref: projectRef, data: project } = await this.loadProjectDoc(projectId);

      const role = resolveBuyerSideRole(project, user.uid);
      if (role === 'none') {
        throw new Error('Not authorized to update this project');
      }

      const restricted = Object.keys(data ?? {}).filter((k) => OWNER_ONLY_FIELDS.has(k));
      if (restricted.length && role !== 'owner') {
        throw new Error(
          'Only the project owner can do that. You can build on this project, but not delete, publish, or re-home it.',
        );
      }

      await updateDoc(projectRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });

      if (data.isDeleted === undefined) {
        this.snack.showSuccess('Project updated successfully');
      }

      return true;
    } catch (err: any) {
      this.snack.showError(`Failed to update project: ${err.message}`);
      throw err;
    }
  }

  /**
   * Hard delete. OWNER ONLY.
   *
   * This read `createdBy` (camelCase) while every real project doc carries
   * `created_by` (snake). On an agent-created project the read returned
   * undefined, `undefined !== user.uid` was always true, and delete threw for
   * everyone including the actual owner. It has been silently broken.
   */
  async deleteProject(projectId: string) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const { ref: projectRef, data: project } = await this.loadProjectDoc(projectId);

      if (!isProjectOwner(project, user.uid)) {
        throw new Error('Only the person who created this project can delete it.');
      }

      await deleteDoc(projectRef);
      this.snack.showSuccess('Project deleted successfully');
    } catch (err: any) {
      this.snack.showError(`Failed to delete project: ${err.message}`);
    }
  }

  /**
   * OWNER ONLY — and it had NO CHECK AT ALL. Not an ownership check, not a
   * membership check, not even a login check. Any caller could point any
   * project's custom domain anywhere.
   */
  async setProjectCustomDomain(
    projectDocId: string,
    domain: string | null,
  ): Promise<void> {
    const user = await firstValueFrom(this.authService.user$);
    if (!user) throw new Error('Not authenticated');

    const { ref: projectRef, data: project } = await this.loadProjectDoc(projectDocId);

    if (!isProjectOwner(project, user.uid)) {
      throw new Error('Only the project owner can set a custom domain.');
    }

    await this.inCtx(async () => {
      await updateDoc(projectRef, {
        customDomain: domain,
        updatedAt: serverTimestamp(),
      });
    });
  }

  async uploadUserPhoto(file: File) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const resizedBlob = await this.resizeImage(file, 60);
      const resizedFile = new File([resizedBlob], file.name, {
        type: 'image/jpeg',
      });

      const storageRef = ref(this.storage, `userPhotos/${user.uid}.jpg`);
      await uploadBytes(storageRef, resizedFile);

      const url = await getDownloadURL(storageRef);
      await this.updateUser({ photoURL: url });

      this.snack.showSuccess('Profile photo updated');
      return url;
    } catch (err: any) {
      this.snack.showError(`Failed to upload photo: ${err.message}`);
      throw err;
    }
  }

  private async resizeImage(file: File, maxSize = 400): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e: any) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas is empty'));
          },
          'image/jpeg',
          0.95,
        );
      };

      img.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  async deleteUserPhoto() {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const storageRef = ref(this.storage, `userPhotos/${user.uid}.jpg`);
      await deleteObject(storageRef);
      await this.updateUser({ photoURL: null });

      this.snack.showSuccess('Profile photo removed');
    } catch (err: any) {
      this.snack.showError(`Failed to delete photo: ${err.message}`);
      throw err;
    }
  }

  async createTicket(
    subject: string,
    description: string,
    projectId: string | null = null,
  ) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const ticketsRef = collection(this.firestore, 'tickets');
      const docRef = await addDoc(ticketsRef, {
        uid: user.uid,
        subject,
        description,
        projectId: projectId || null,
        status: 'open',
        createdAt: serverTimestamp(),
      });

      this.snack.showSuccess('Ticket submitted successfully');
      return docRef.id;
    } catch (err: any) {
      this.snack.showError(`Failed to create ticket: ${err.message}`);
      throw err;
    }
  }

  getMyTickets(): Observable<any[]> {
    if (this.isServer) return of([]);

    return this.authService.user$.pipe(
      filter((user): user is User => !!user),
      switchMap((user) =>
        this.inCtx(() => {
          const q = query(
            collection(this.firestore, 'tickets'),
            where('uid', '==', user.uid),
          );
          return collectionData(q, { idField: 'id' }) as Observable<any[]>;
        }),
      ),
      map((tickets) => tickets.filter((t) => !t.isDeleted)),
    );
  }

  async updateTicketStatus(ticketId: string, status: 'open' | 'closed') {
    try {
      const ticketRef = doc(this.firestore, 'tickets', ticketId);
      const data: any = { status };

      if (status === 'closed') {
        data.closedAt = serverTimestamp();
      } else {
        data.reopenedAt = serverTimestamp();
      }

      await updateDoc(ticketRef, data);
      this.snack.showSuccess(`Ticket marked as ${status}`);
    } catch (err: any) {
      this.snack.showError(`Failed to update ticket: ${err.message}`);
    }
  }

  async softDeleteTicket(ticketId: string) {
    try {
      const ticketRef = doc(this.firestore, 'tickets', ticketId);
      await updateDoc(ticketRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
      });
      this.snack.showSuccess('Ticket deleted successfully');
    } catch (err: any) {
      this.snack.showError(`Failed to delete ticket: ${err.message}`);
    }
  }

  async createFeedback(
    projectId: string | null,
    rating: number,
    message: string,
  ) {
    try {
      const user = await firstValueFrom(this.authService.user$);
      if (!user) throw new Error('Not authenticated');

      const feedbackRef = collection(this.firestore, 'feedback');
      await addDoc(feedbackRef, {
        uid: user.uid,
        projectId: projectId || null,
        rating,
        message,
        createdAt: serverTimestamp(),
      });

      this.snack.showSuccess('Feedback submitted successfully');
    } catch (err: any) {
      this.snack.showError(`Failed to submit feedback: ${err.message}`);
      throw err;
    }
  }

  getMyFeedback(): Observable<any[]> {
    if (this.isServer) return of([]);

    return this.authService.user$.pipe(
      filter((user): user is User => !!user),
      switchMap((user) =>
        this.inCtx(() => {
          const q = query(
            collection(this.firestore, 'feedback'),
            where('uid', '==', user.uid),
          );
          return collectionData(q, { idField: 'id' }) as Observable<any[]>;
        }),
      ),
    );
  }

  getMessagesByProjectIdStream(projectId: string): Observable<any[]> {
    if (this.isServer) return of([]);

    return new Observable<any[]>((observer) => {
      const unsubscribeMessages = this.inCtx(() => {
        const q = query(
          collection(this.firestore, 'messages'),
          where('project_id', '==', projectId),
          orderBy('created_at', 'asc'),
        );

        return onSnapshot(
          q,
          (snapshot) => {
            if (snapshot.empty) {
              observer.next([]);
              return;
            }

            const messageStreams = snapshot.docs.map((docSnap) => {
              const msg: any = { id: docSnap.id, ...docSnap.data() };

              if (msg.role !== 'assistant') {
                return of({ ...msg, logs: [] });
              }

              return new Observable<any>((logObserver) => {
                const unsubscribeLogs = this.inCtx(() =>
                  onSnapshot(
                    collection(this.firestore, `messages/${msg.id}/logs`),
                    (logSnap) => {
                      const logs = logSnap.docs.map((l) => ({
                        id: l.id,
                        ...l.data(),
                      }));
                      logObserver.next({ ...msg, logs });
                    },
                    (err) => logObserver.error(err),
                  ),
                );
                return () => unsubscribeLogs();
              });
            });

            combineLatest(messageStreams).subscribe({
              next: (fullMessages) => observer.next(fullMessages),
              error: (err) => observer.error(err),
            });
          },
          (error) => observer.error(error),
        );
      });

      return () => unsubscribeMessages();
    });
  }

  /**
   * The live project doc, gated on real access.
   *
   * TWO THINGS WERE WRONG HERE.
   *
   * 1. `const isOwner = userIds.includes(user.uid)` — that is MEMBERSHIP, not
   *    ownership, and calling it isOwner is how the confusion spread through
   *    the codebase. The emitted doc now carries __role so callers can tell
   *    the difference instead of guessing.
   *
   * 2. Partner access required an ASSIGNED TICKET. But a partner is staffed
   *    onto projectMembers at QUOTE time and gets no tickets until the buyer
   *    funds and an admin breaks the work up. In that window they were staffed,
   *    they could see the project in their list — and opening it threw "You do
   *    not have access to this project."  ProjectAccessService accepts EITHER
   *    a projectMembers row OR a ticket, which closes that window.
   */
  getProjectDocStream(projectId: string): Observable<any | null> {
    if (this.isServer) return of(null);

    return new Observable<any | null>((observer) => {
      const user = this.authService.user.value;

      if (!user) {
        observer.error(new Error('User not authenticated'));
        return;
      }

      const unsubscribe = this.inCtx(() => {
        const q = query(
          collection(this.firestore, 'projects'),
          where('project_id', '==', projectId),
        );

        return onSnapshot(
          q,
          async (snapshot) => {
            if (snapshot.empty) {
              observer.error(new Error('Project not found'));
              return;
            }

            const docSnap = snapshot.docs[0];
            const data = docSnap.data();

            const access = await this.access.resolveFrom(
              data as any,
              user.uid,
              user.email,
              projectId,
            );

            if (access.role === 'none') {
              observer.error(
                new Error('You do not have access to this project'),
              );
              return;
            }

            observer.next({
              id: docSnap.id,
              ...data,
              __role: access.role,
              __canDelete: access.can.canAdminister,
              __canAdminister: access.can.canAdminister,
              __canFund: access.can.canFund,
              __canRequestEngagement: access.can.canRequestEngagement,
              __canManageTeam: access.can.canManageTeam,
              __collaboratorId: access.collaboratorId ?? null,
            });
          },
          (error) => observer.error(error),
        );
      });

      return () => unsubscribe();
    });
  }

  async generateAndSaveThumbnail(
    projectId: string,
    vercelUrl: string,
  ): Promise<void> {
    const cloudRunUrl = `https://thumbnail-service-861501658886.us-central1.run.app/generate?url=${vercelUrl}`;

    const imageBlob = await firstValueFrom(
      this.http.get(cloudRunUrl, { responseType: 'blob' }),
    );

    const storageRef = ref(this.storage, `thumbnails/${projectId}.png`);
    await uploadBytes(storageRef, imageBlob);

    const downloadUrl = await getDownloadURL(storageRef);

    const projectRef = doc(this.firestore, `projects/${projectId}`);
    await updateDoc(projectRef, {
      thumbnail_url: downloadUrl,
      thumbnail_generated_at: new Date(),
    });
  }

  private communityBaseConstraints(): QueryConstraint[] {
    return [orderBy('updated_at', 'desc')];
  }

  async fetchCommunityPage(
    pageSize: number,
    cursor: CommunityCursor,
    excludeUid: string | null,
  ): Promise<CommunityPage> {
    if (this.isServer) {
      return { items: [], cursor: null, exhausted: true };
    }

    const batchSize = pageSize * COMMUNITY_OVERFETCH_FACTOR;

    const collected: any[] = [];
    let currentCursor: CommunityCursor = cursor;
    let exhausted = false;
    let rounds = 0;

    while (
      collected.length < pageSize &&
      !exhausted &&
      rounds < COMMUNITY_MAX_TOPUP_ROUNDS
    ) {
      rounds++;

      const snapshot = await this.inCtx(() => {
        const constraints: QueryConstraint[] = [
          ...this.communityBaseConstraints(),
          ...(currentCursor ? [startAfter(currentCursor)] : []),
          limit(batchSize),
        ];
        return getDocs(
          query(collection(this.firestore, 'projects'), ...constraints),
        );
      });

      if (snapshot.empty) {
        exhausted = true;
        break;
      }

      if (snapshot.docs.length < batchSize) {
        exhausted = true;
      }

      currentCursor = snapshot.docs[snapshot.docs.length - 1];

      for (const d of snapshot.docs) {
        const project = { docId: d.id, ...d.data() } as any;
        if (isVisibleCommunityProject(project, excludeUid)) {
          collected.push(project);
        }
      }
    }

    return {
      items: collected,
      cursor: currentCursor,
      exhausted,
    };
  }

  getAllProjects(): Observable<any[]> {
    if (this.isServer) return of([]);

    return this.inCtx(() => {
      const q = query(
        collection(this.firestore, 'projects'),
        ...this.communityBaseConstraints(),
      );
      return collectionData(q, { idField: 'docId' }) as Observable<any[]>;
    }).pipe(map((projects) => [...projects].sort(byUpdatedAtDesc)));
  }
}